import React, { useEffect, useState } from 'react';
import { API_BASE } from '../config/api';
import '../styles/Common.css';

function RomanOptionSection({ title, apiPath, includeHeight }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    id: null,
    name: '',
    height: '',
    description: '',
    is_active: true,
  });
  const [reloadFlag, setReloadFlag] = useState(0); // 목록 새로고침용 트리거

  const isEditing = form.id !== null;

  // 🔹 목록 조회 (apiPath 또는 reloadFlag 바뀔 때마다 실행)
  useEffect(() => {
    const fetchList = async () => {
      try {
        setLoading(true);
        setError('');

        const res = await fetch(
          `${API_BASE}${apiPath}?include_inactive=1`
        );
        if (!res.ok) {
          throw new Error(`목록 조회 실패 (status: ${res.status})`);
        }
        const data = await res.json();
        setItems(data);
      } catch (e) {
        console.error(e);
        setError(e.message || '목록을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchList();
  }, [apiPath, reloadFlag]); // ✅ eslint 경고 안 뜸

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const resetForm = () => {
    setForm({
      id: null,
      name: '',
      height: '',
      description: '',
      is_active: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const payload = {
        name: form.name || null,
        description: form.description || null,
        is_active: form.is_active ? 1 : 0,
      };

      if (includeHeight) {
        payload.height = form.height ? Number(form.height) : null;
      }

      const url = isEditing
        ? `${API_BASE}${apiPath}/${form.id}`
        : `${API_BASE}${apiPath}`;
      const method = isEditing ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`저장 실패 (status: ${res.status})\n${text || ''}`);
      }

      // 목록 다시 불러오기
      setReloadFlag((prev) => prev + 1);
      resetForm();
    } catch (e) {
      console.error(e);
      setError(e.message || '저장 중 오류가 발생했습니다.');
    }
  };

  const handleEdit = (item) => {
    setForm({
      id: item.id,
      name: item.name ?? '',
      height: item.height ?? '',
      description: item.description ?? '',
      is_active: item.is_active === 1,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      const res = await fetch(`${API_BASE}${apiPath}/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`삭제 실패 (status: ${res.status})\n${text || ''}`);
      }
      // 삭제 후 목록 다시 불러오기
      setReloadFlag((prev) => prev + 1);
    } catch (e) {
      console.error(e);
      setError(e.message || '삭제 중 오류가 발생했습니다.');
    }
  };

  return (
    <section className="roman-section">
      <div className="roman-card">
        <div className="roman-card-header">
          <h2>{title}</h2>
          <span className="roman-count-badge">{items.length}</span>
        </div>

        {/* ===== 입력 폼 ===== */}
        <form className="roman-form" onSubmit={handleSubmit}>
          <div className="form-field">
            <label>이름</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="이름을 입력하세요"
            />
          </div>

          {includeHeight && (
            <div className="form-field">
              <label>높이 (mm)</label>
              <input
                type="number"
                name="height"
                value={form.height}
                onChange={handleChange}
                placeholder="예: 1200"
              />
            </div>
          )}

          <div className="form-field form-field-full">
            <label>설명</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="설명을 입력하세요"
              rows={3}
            />
          </div>

          {/* is_active 토글 */}
          <div className="form-field">
            <label>사용 여부</label>
            <label className="toggle-label">
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={handleChange}
              />
              <span className="toggle-text">
                {form.is_active ? '사용' : '비활성'}
              </span>
            </label>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn primary">
              {isEditing ? '수정하기' : '추가하기'}
            </button>
            {isEditing && (
              <button type="button" className="btn ghost" onClick={resetForm}>
                새로 입력
              </button>
            )}
          </div>

          {error && <div className="error-text">{error}</div>}
        </form>

        {/* ===== 목록 ===== */}
        <div className="roman-table-wrapper">
          {loading ? (
            <div className="loading-text">불러오는 중...</div>
          ) : items.length === 0 ? (
            <div className="empty-text">데이터가 없습니다.</div>
          ) : (
            <table className="roman-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>이름</th>
                  {includeHeight && <th>높이</th>}
                  <th>설명</th>
                  <th>상태</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td data-label="ID">{item.id}</td>
                    <td data-label="이름">{item.name}</td>
                    {includeHeight && (
                      <td data-label="높이">{item.height}</td>
                    )}
                    <td data-label="설명" className="roman-desc-cell">
                      {item.description}
                    </td>
                    <td data-label="상태">
                      {item.is_active === 1 ? (
                        <span className="status-badge active">사용</span>
                      ) : (
                        <span className="status-badge inactive">비활성</span>
                      )}
                    </td>
                    <td data-label="관리">
                      <div className="row-actions">
                        <button
                          type="button"
                          className="btn tiny"
                          onClick={() => handleEdit(item)}
                        >
                          수정
                        </button>
                        <button
                          type="button"
                          className="btn tiny danger"
                          onClick={() => handleDelete(item.id)}
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </section>
  );
}

const RomanSetPage = () => {
  const [activeTab, setActiveTab] = useState('head');

  const tabs = [
    {
      id: 'head',
      label: '헤드레일',
      apiPath: '/api/rm_headrail',
      includeHeight: true,
    },
    {
      id: 'middle',
      label: '미들',
      apiPath: '/api/rm_middle',
      includeHeight: true,
    },
    {
      id: 'bottom',
      label: '하단바',
      apiPath: '/api/rm_bottombar',
      includeHeight: true, // 하단바도 높이 사용
    },
  ];

  const currentTab = tabs.find((t) => t.id === activeTab) || tabs[0];

  return (
    <div className="common-container roman-settings">
      <h1 className="page-title">로만 쉐이드 설정 페이지</h1>

      {/* 탭 헤더 */}
      <div className="roman-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={
              'roman-tab-button' + (tab.id === activeTab ? ' active' : '')
            }
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 선택된 탭 하나만 렌더링 */}
      <RomanOptionSection
        title={currentTab.label}
        apiPath={currentTab.apiPath}
        includeHeight={currentTab.includeHeight}
      />
    </div>
  );
};

export default RomanSetPage;
